import React, { forwardRef, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { FaRegCopy } from "react-icons/fa";

import { useAppContext } from "../../../context/AppContext";
import useLocalization from "../../../hooks/useLocalization";

import BotErrorBox from "./BotResponseComponent/BotErrorBox";
import BotResponseText from "./BotResponseComponent/BotResponseText";
import GrievanceInfoCard from "./BotResponseComponent/GrievanceInfoCard";
import ImageResponse from "./BotResponseComponent/ImageResponse";
import AudioResponsePlayer from "./BotResponseComponent/AudioResponsePlayer";
import StatusAnimator from "./BotResponseComponent/StatusAnimator";
import LanguageDetector from "./BotResponseComponent/LanguageDetector";
import { useLogger } from "../../../utils/UnifiedLogger";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";

import {
  setInputDisabled,
  setInputValue,
  setMicButtonDisabled,
  setSendButtonDisabled,
} from "../../../store/features/ui/uiSlice";
import { selectSelectedChatId } from "../../../store/features/message/textContentSelectors";
import {
  setIsHideUserInputFields,
  unlockTextFlowForChat,
  unlockVoiceFlowForChat,
} from "../../../store/features/message/textContentSlice";
import { updateFlowStates } from "../../../store/features/message/updateFlowStates";
import BotErrorMessage from "./BotResponseComponent/BotErrorMessage";
import GrivanceNotRelatedToCpgram from "./BotResponseComponent/GrivanceNotRelatedToCpgram";
import VerifyDataModal from "./BotResponseComponent/VerifyDataModel/VerifyDataModal";
import BotDynamicForm from "./BotResponseComponent/BotDynamicForm/BotDynamicForm";
import StateRelatedGrievance from "./BotResponseComponent/StateRelatedGrievance";
import VoiceJourneyMuteUnmuteBgButton from "./BotResponseComponent/VoiceJourneyMuteUnmuteBgButton";
import { isFormAvailable, isValidFieldForm } from "../../../utils/util";
import { getChatConfig } from "../../../utils/chatConfigStorage";

const BotChatHead = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const chatConfig = getChatConfig();
  const bottomRef = useRef(null);
  const { logInfo } = useLogger("BOT_CHAT_HEAD", false);
  const { selectedLanguageData, userData } = useAppContext();
  const selectedChatId = useSelector(selectSelectedChatId);

  const { t } = useLocalization();
  const [showText, setShowText] = useState(true);

  const {
    response = {},
    onRegenerate,
    onCopy,
    timestamp = "",
    botImage = "",
    identificationClassName = "",
    hasSubmitted,
    handleSubmitGrievance,
    setFormAnswers,
    setFilledForms,
    onDynamicFormSubmit,
    setDynamicFormSubmit,
    isMostRecent = false,
    handleSend,
    chatData,
  } = props;

  const {
    contentAudio = "",
    text = "",
    contentStandard = "",
    imageUrl = "",
    mandatory_data_present,
    mandatory_questions_answered,
    mandatory_questions_asked,
    grievance_bucket,
    updated_grievance = "",
    botResponse,
    isLanguageDetecting = false,
    isBotResponding = false,
    languageDetectedMessage = "",
    isError = false,
    field_form = {},
    nerContent,
    isNerResponding,
    apiProgressData,
    category_code = null,
    org_code = "",
    field_form_list = [],
    errorMessage = "",
    isErrorMessage = false,
    isNotRelatedToGrievance = false,
    isNotRelatedToCenter = false,
    serverRemarks = "",
    user_query = "",
    stateName = "",
    stateCode = "",
    stateName_regional = "",
    isNotSubcategory = false,
    isGrievanceSubmitted = false,
  } = response;

  const isFieldFormContainsData = () => {
    const isValidForm =
      field_form &&
      typeof field_form === "object" &&
      !field_form.error &&
      !field_form.status_code &&
      Array.isArray(field_form.InputFields) &&
      field_form.InputFields.length > 0;
    return isValidForm;
  };

  // ✅ Show form only when mandatory data not present & valid form exists
  const shouldShowDynamicForm =
    isValidFieldForm(chatData) && isFormAvailable(chatData); //!isVoiceFlow && !isVoiceFlowLocked && !mandatory_data_present && isFieldFormContainsData() && !isNotRelatedToGrievance && !isNotRelatedToCenter;

  const isValidResponseText =
    typeof text === "string" &&
    text.trim().length > 0 &&
    contentStandard.toLowerCase().trim() !== "only form fields" &&
    !isNotRelatedToGrievance &&
    !isNotRelatedToCenter;
  const shouldShowTextResponse = isValidResponseText;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const shouldHide =
      isNotRelatedToGrievance || isNotRelatedToCenter || isErrorMessage;
    dispatch(setInputDisabled(shouldHide));
    dispatch(setSendButtonDisabled(shouldHide));
    dispatch(setMicButtonDisabled(shouldHide));
  }, [isNotRelatedToGrievance, isNotRelatedToCenter, isErrorMessage, dispatch]);

  useEffect(() => {
    if (shouldShowDynamicForm) {
      dispatch(setIsHideUserInputFields(true));
    } else {
      dispatch(setIsHideUserInputFields(false));
    }
  }, [shouldShowDynamicForm, shouldShowTextResponse, dispatch]);

  const handleCopy = async () => {
    const element = document.getElementById("grievance-content");
    if (element) {
      const htmlToCopy = element.innerHTML;
      const textToCopy = element.innerText;

      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([htmlToCopy], { type: "text/html" }),
          "text/plain": new Blob([textToCopy], { type: "text/plain" }),
        }),
      ]);
    }
  };

  const handleSendSelectedText = (selectedText) => {
    logInfo("SELECTED_TEXT_SENT", "User sent selected text:", { selectedText });
    if (selectedText) {
      dispatch(setInputValue(selectedText));
      dispatch(updateFlowStates());
      handleSend();
    }
  };

  return (
    <motion.div
      ref={ref}
      id={props.id}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={`flex items-start w-full max-w-2xl px-2 py-4 mb-10 ${identificationClassName}`}
    >
      <div className="w-10 h-10 mt-0 mr-2 rounded-full hidden sm:flex items-center justify-center bg-[#EA580C] shrink-0 overflow-hidden">
        <img
          src={botImage}
          alt="Bot"
          className="w-full h-full object-cover"
        />
      </div>

      {isError ? (
        <BotErrorBox t={t} onRegenerate={onRegenerate} />
      ) : (
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="flex flex-col flex-1 bg-white w-full dark:bg-[#232323] border border-gray-200 rounded-xl shadow-lg text-gray-900 dark:text-white p-4"
        >
          {(isLanguageDetecting || languageDetectedMessage) && (
            <div className="relative">
              <LanguageDetector
                isLanguageDetecting={isLanguageDetecting}
                languageDetectedMessage={languageDetectedMessage}
              />

              <VoiceJourneyMuteUnmuteBgButton
                isVoiceFlow={chatConfig?.journey === "VOICE"}
                isVoiceFlowLocked={chatConfig?.journeyLocked}
              />
            </div>
          )}

          {isBotResponding && (
            <StatusAnimator
              nerContent={nerContent}
              isNerResponding={isNerResponding}
              isApiResponding={isBotResponding}
              progressData={apiProgressData}
              isVoiceFlow={chatConfig?.journey === "VOICE"}
            />
          )}

          {chatConfig?.journey === "VOICE" &&
            (!shouldShowDynamicForm || mandatory_data_present) && (
              <AudioResponsePlayer
                isAutoPlay={isMostRecent && chatConfig?.journey === "VOICE"}
                contentAudio={contentAudio}
                showText={showText}
                setShowText={setShowText}
                isLoading={false}
              />
            )}

          {/* {console.log({
            showText,
            text,
            isValidFieldForm: !isValidFieldForm(chatData),
            mandatory_data_present: !mandatory_data_present,
            shouldShowTextResponse,
            isNotRelatedToGrievance: !isNotRelatedToGrievance,
            isNotRelatedToCenter: !isNotRelatedToCenter,
          })} */}

          {showText &&
            shouldShowTextResponse &&
            !isValidFieldForm(chatData) && (
              <BotResponseText
                text={text}
                onSendSelection={handleSendSelectedText}
              />
            )}

          {text &&
            contentStandard === "only form fields" &&
            !isValidFieldForm(chatData) && (
              <div className="text-gray-500 dark:text-gray-400 italic">
                {t("noResponse") || "No text response available."}
              </div>
            )}

          {shouldShowDynamicForm && !mandatory_data_present && (
            <BotDynamicForm
              field_form={isFieldFormContainsData() ? field_form : {}}
              field_form_list={
                field_form_list.length > 0 ? field_form_list : []
              }
              category_code={category_code}
              org_code={org_code}
              formValues={formValues}
              setFormValues={setFormValues}
              errors={errors}
              setErrors={setErrors}
              setFormAnswers={setFormAnswers}
              setFilledForms={setFilledForms}
              onDynamicFormSubmit={onDynamicFormSubmit}
              setDynamicFormSubmit={setDynamicFormSubmit}
            />
          )}

          {mandatory_data_present &&
            grievance_bucket &&
            Object.keys(grievance_bucket).length > 0 && (
              <div id="grievance-content">
                <GrievanceInfoCard
                  category_code={category_code}
                  isNotSubcategory={isNotSubcategory}
                  field_form_list={field_form_list}
                  grievance_bucket={grievance_bucket}
                  updated_grievance={updated_grievance}
                  selectedLanguageCode={selectedLanguageData?.code || "en"}
                  mandatory_questions_answered={mandatory_questions_answered}
                  mandatory_questions_asked={mandatory_questions_asked}
                  onVerifyClick={() => {
                    setIsModalOpen(true);
                    if (
                      chatConfig?.journey === "VOICE" &&
                      mandatory_data_present === true
                    ) {
                      dispatch(unlockVoiceFlowForChat(selectedChatId));
                    }

                    if (
                      chatConfig?.journey === "TEXT" &&
                      mandatory_data_present === true
                    ) {
                      dispatch(unlockTextFlowForChat(selectedChatId));
                    }
                  }}
                  field_form={isFieldFormContainsData() ? field_form : {}}
                  isMostRecent={isMostRecent}
                  contentAudio={contentAudio}
                  mandatory_data_present={mandatory_data_present}
                  isGrievanceSubmitted={isGrievanceSubmitted}
                />
              </div>
            )}

          <ImageResponse imageUrl={imageUrl} />

          <BotErrorMessage
            isErrorMessage={isErrorMessage}
            errorMessage={errorMessage}
          />

          <GrivanceNotRelatedToCpgram
            isNotRelatedToGrievance={isNotRelatedToGrievance}
            message={text}
          />

          <StateRelatedGrievance
            isNotRelated={isNotRelatedToCenter}
            message={text}
            userQuery={user_query}
            stateName={stateName}
            stateName_regional={stateName_regional}
            stateCode={stateCode}
          />

          {chatConfig?.journey === "TEXT" &&
            (!shouldShowDynamicForm || mandatory_data_present) && (
              <AudioResponsePlayer
                isAutoPlay={chatConfig?.journey === "VOICE" && isMostRecent}
                contentAudio={contentAudio}
                showText={showText}
                setShowText={setShowText}
                isLoading={false}
              />
            )}

          <div className="flex items-center justify-between mt-2 border-t pt-3">
            <div className="flex space-x-2">
              <button
                onClick={mandatory_data_present ? handleCopy : onCopy}
                title="Copy"
                className="text-gray-600 hover:text-black dark:text-gray-400 dark:hover:text-white"
              >
                <FaRegCopy />
              </button>
            </div>
            <span className="text-xs text-[#9F2D00] dark:text-[#9F2D00]">
              {timestamp}
            </span>
          </div>

          {isModalOpen && (
            <VerifyDataModal
              isNotSubcategory={isNotSubcategory}
              category_code={category_code}
              org_code={org_code}
              isOpen={isModalOpen}
              closeModal={() => setIsModalOpen(false)}
              onClose={() => setIsModalOpen(false)}
              userData={userData}
              grievance_bucket={grievance_bucket}
              updated_grievance={updated_grievance}
              handleSubmitGrievance={handleSubmitGrievance}
              hasSubmitted={hasSubmitted}
              mandatory_questions_answered={
                typeof mandatory_questions_answered !== "string"
                  ? mandatory_questions_answered
                  : []
              }
              mandatory_questions_asked={
                typeof mandatory_questions_asked !== "string"
                  ? mandatory_questions_asked
                  : []
              }
              mandatory_data_present={mandatory_data_present}
              botResponse={botResponse}
              field_form={field_form}
              field_form_list={field_form_list}
              serverRemarks={serverRemarks}
            />
          )}
        </motion.div>
      )}

      <div ref={bottomRef} data-anchor="bot-bottom" />
    </motion.div>
  );
});

BotChatHead.propTypes = {
  response: PropTypes.object,
  onRegenerate: PropTypes.func,
  onCopy: PropTypes.func,
  timestamp: PropTypes.string,
  botImage: PropTypes.string,
  identificationClassName: PropTypes.string,
  hasSubmitted: PropTypes.bool,
  handleSubmitGrievance: PropTypes.func,
  setFormAnswers: PropTypes.func,
  setFilledForms: PropTypes.func,
  onDynamicFormSubmit: PropTypes.bool,
  setDynamicFormSubmit: PropTypes.func,
  isMostRecent: PropTypes.bool,
  handleSend: PropTypes.func,
};
BotChatHead.displayName = "BotChatHead";
export default BotChatHead;
